// import { useEffect, useMemo, useState } from "react";
// import axios from "axios";
// import "./Gallery.css";
// import { webServiceUrl } from "../../../../components/apiCall/webserviceurl";

// /**
//  * Gallery
//  * ────────────────────────────────────────────────────────────────
//  * A day-by-day photo journal.
//  *
//  * - Top nav shows chips for every day. Clicking a chip FILTERS the
//  *   grid down to that single day (no more scroll-to-section — only
//  *   the selected day's photos render below).
//  * - The photo grid is a masonry / justified layout (CSS columns),
//  *   like Google Images or Pinterest: each photo keeps its own
//  *   natural aspect ratio instead of being cropped into a fixed box,
//  *   so portrait shots stay tall and landscape shots stay wide.
//  *
//  * Days come from POST /getLessonPlanDays, and each day's photos come
//  * from GET /get-gallery-photo?day=<date> (fetched lazily, the first
//  * time that day's chip is opened).
//  */

// // Cycles through these for any number of days — accent + text pairs
// const DAY_COLORS = [
//   { bg: "#16a34a", text: "#fff" }, // dawn amber — Day 1
//   { bg: "#16a34a", text: "#fff" }, // meadow teal — Day 2
//   { bg: "#16a34a", text: "#fff" }, // dusk indigo — Day 3
//   { bg: "#16a34a", text: "#fff" }, // clay rose — Day 4
// ];

// // Fixed identity payload used by /getLessonPlanDays.
// const LESSON_PLAN_IDENTITY = {
//   class_name: "ALL",
//   section: "A",
//   level: "FACULTY",
//   combination_code: "UBTE_FDP4",
//   planned_on: "2026-03-26",
//   school_code: "900",
// };

// // A photo item from /get-gallery-photo might come back as a plain URL
// // string or as an object with the URL under some field — this covers
// // both without guessing wrong. Adjust the object-field fallbacks below
// // once you see the real response shape.
// const normalizePhotoUrl = (photo) => {
//   if (typeof photo === "string") return photo;
//   return photo?.url || photo?.image || photo?.photo_url || photo?.src || "";
// };

// export default function Gallery() {
//   let token = localStorage.getItem("token");

//   const [days, setDays] = useState([]);
//   const [daysLoading, setDaysLoading] = useState(true);
//   const [photosLoading, setPhotosLoading] = useState(false);
//   const [selectedDay, setSelectedDay] = useState("");
//   const [lightboxIndex, setLightboxIndex] = useState(null); // index into the CURRENT day's photos | null

//   // NULL-SAFE: currentDay can legitimately be null (before days load,
//   // or if the API returns zero days) — every consumer below must
//   // handle that instead of assuming days[0] always exists.
//   const currentDay = useMemo(
//     () => days.find((d) => d.id === selectedDay) ?? days[0] ?? null,
//     [days, selectedDay],
//   );
//   const currentDayIndex = currentDay
//     ? days.findIndex((d) => d.id === currentDay.id)
//     : -1;
//   const currentColor =
//     DAY_COLORS[currentDayIndex % DAY_COLORS.length] ?? DAY_COLORS[0];

//   // ---------------------------------------------------------------------
//   // DAYS — POST /getLessonPlanDays
//   // Returns the list of dates for the program. Each date becomes a chip
//   // ("Day 1", "Day 2", ...); its photos are loaded lazily (see below).
//   // ---------------------------------------------------------------------
//   const getLessonPlanDays = async () => {
//     try {
//       setDaysLoading(true);
//       const res = await axios.post(
//         webServiceUrl.getLessonPlanDays,
//         LESSON_PLAN_IDENTITY,
//         { headers: { Authorization: `Bearer ${token}` } },
//       );

//       // Real shape: { success: true, data: [{ day: "2025-08-28" }, ...] }
//       const rawDays = res?.data?.data || [];

//       if (Array.isArray(rawDays) && rawDays.length > 0) {
//         const mappedDays = rawDays.map((d, idx) => ({
//           id: `day-${idx + 1}`,
//           title: `Day ${idx + 1}`,
//           date: d?.day || "", // "YYYY-MM-DD" — passed straight to /get-gallery-photo
//           photos: [], // filled in by loadPhotosForDay() below
//         }));
//         setDays(mappedDays);
//         setSelectedDay(mappedDays[0].id);
//         // Load the first day's photos right away.
//         loadPhotosForDay(mappedDays[0].id, mappedDays[0].date);
//       } else {
//         // API responded but with no days — make sure we don't stay
//         // stuck thinking we still have data coming.
//         setDays([]);
//       }
//     } catch (e) {
//       console.log(e, "ERROR coming from getLessonPlanDays");
//       setDays([]);
//     } finally {
//       setDaysLoading(false);
//     }
//   };

//   // ---------------------------------------------------------------------
//   // PHOTOS — GET /get-gallery-photo?day=<date>
//   // Fetched lazily: only the first time a given day's chip is opened,
//   // then cached on that day's `photos` array.
//   // ---------------------------------------------------------------------
//   const getGalleryPhotos = async (dateStr) => {
//     if (!dateStr) return [];
//     try {
//       const res = await axios.get(
//         `${webServiceUrl.get_gallery_photo}?day=${dateStr}`,
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         },
//       );

//       console.log(res, "<<<<<<photos>>>>>>>");
//       // NOTE: adjust the field name below (`res.data.data`) to match the
//       // real /get-gallery-photo response shape.
//       const rawPhotos = res?.data?.data || res?.data?.photos || [];
//       return Array.isArray(rawPhotos) ? rawPhotos.map(normalizePhotoUrl) : [];
//     } catch (e) {
//       console.log(e, "ERROR coming from getGalleryPhotos");
//       return [];
//     }
//   };

//   const loadPhotosForDay = async (dayId, dateStr) => {
//     if (!dateStr) return;
//     setPhotosLoading(true);
//     const photos = await getGalleryPhotos(dateStr);
//     if (photos.length > 0) {
//       setDays((prev) =>
//         prev.map((d) => (d.id === dayId ? { ...d, photos } : d)),
//       );
//     }
//     setPhotosLoading(false);
//   };

//   const handleSelectDay = (day) => {
//     setSelectedDay(day.id);
//     if (day?.photos?.length === 0) {
//       loadPhotosForDay(day.id, day.date);
//     }
//   };

//   useEffect(() => {
//     getLessonPlanDays();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   // Reset the lightbox whenever the selected day changes.
//   useEffect(() => {
//     setLightboxIndex(null);
//   }, [selectedDay]);

//   // Lightbox keyboard controls: Escape closes, arrows step through
//   // photos within the currently selected day.
//   useEffect(() => {
//     if (lightboxIndex === null || !currentDay) return;
//     const total = currentDay?.photos?.length;
//     if (!total) return;
//     const onKeyDown = (e) => {
//       if (e.key === "Escape") setLightboxIndex(null);
//       if (e.key === "ArrowRight") setLightboxIndex((i) => (i + 1) % total);
//       if (e.key === "ArrowLeft")
//         setLightboxIndex((i) => (i - 1 + total) % total);
//     };
//     window.addEventListener("keydown", onKeyDown);
//     return () => window.removeEventListener("keydown", onKeyDown);
//   }, [lightboxIndex, currentDay]);

//   console.log(currentDay);

//   return (
//     <div className="gal-page">
//       <div className="gal-panel">
//         {/* <h2 className="gal-panel-title">Gallery</h2> */}

//         <div className="gal-nav" role="tablist" aria-label="Choose a day">
//           {days.map((day, i) => {
//             const color = DAY_COLORS[i % DAY_COLORS.length];
//             const isActive = selectedDay === day.id;
//             return (
//               <div className="gal-nav-item" key={day.id}>
//                 <button
//                   role="tab"
//                   aria-selected={isActive}
//                   className={`gal-nav-chip${isActive ? " gal-nav-chip--active" : ""}`}
//                   style={{ "--accent": color.bg }}
//                   onClick={() => handleSelectDay(day)}
//                 >
//                   {day?.title}
//                   <span className="gal-nav-chip-count">
//                     {day?.photos?.length}
//                   </span>
//                 </button>
//                 {i < days?.length - 1 && (
//                   <ArrowIcon className="gal-nav-arrow" />
//                 )}
//               </div>
//             );
//           })}
//         </div>

//         {/* NULL-SAFE: bail out here instead of ever touching
//             currentDay.id when currentDay is null (still loading, or
//             the API returned zero days). This is what was crashing. */}
//         {!currentDay ? (
//           <div
//             style={{
//               textAlign: "center",
//               padding: "40px 0",
//               color: "#6b7280",
//             }}
//           >
//             {daysLoading ? "Loading days…" : "No gallery days found."}
//           </div>
//         ) : (
//           <section className="gal-day" key={currentDay.id}>
//             {/* <header className="gal-day-header">
//               <span
//                 className="gal-day-tab"
//                 style={{ background: currentColor.bg, color: currentColor.text }}
//               >
//                 {String(currentDayIndex + 1).padStart(2, "0")}
//               </span>
//               <div className="gal-day-heading">
//                 <h3 className="gal-day-title">{currentDay.title}</h3>
//                 <span className="gal-day-meta">
//                   {currentDay.photos.length} photo
//                   {currentDay.photos.length === 1 ? "" : "s"}
//                 </span>
//               </div>
//             </header> */}

//             {photosLoading && currentDay?.photos?.length === 0 ? (
//               <div
//                 style={{
//                   textAlign: "center",
//                   padding: "40px 0",
//                   color: "#6b7280",
//                 }}
//               >
//                 Loading photos…
//               </div>
//             ) : (
//               <div className="gal-day-grid">
//                 {currentDay?.photos?.length === 0 ? (
//                   <div
//                     style={{
//                       textAlign: "center",
//                       padding: "40px 0",
//                       color: "#9ca3af",
//                       fontSize: "16px",
//                       fontWeight: "500",
//                     }}
//                   >
//                     No images found for {currentDay.title}
//                   </div>
//                 ) : (
//                   <div className="gal-day-grid">
//                     {currentDay.photos.map((src, j) => (
//                       <button
//                         type="button"
//                         className="gal-day-item"
//                         key={j}
//                         style={{ "--accent": currentColor.bg }}
//                         onClick={() => setLightboxIndex(j)}
//                         aria-label={`Open photo ${j + 1} from ${currentDay.title}`}
//                       >
//                         <img
//                           src={src}
//                           alt={`${currentDay.title} photo ${j + 1}`}
//                           loading="lazy"
//                         />
//                         <span className="gal-day-item-caption">
//                           {currentDay.title} · {j + 1}
//                         </span>
//                       </button>
//                     ))}
//                   </div>
//                 )}
//               </div>
//             )}
//           </section>
//         )}
//       </div>

//       {/* NULL-SAFE: only render the lightbox if we actually have a
//           currentDay to reference. */}
//       {lightboxIndex !== null && currentDay && (
//         <div
//           className="gal-lightbox"
//           onClick={() => setLightboxIndex(null)}
//           role="dialog"
//           aria-modal="true"
//           aria-label={`${currentDay.title} photo ${lightboxIndex + 1}`}
//         >
//           <button
//             className="gal-lightbox-close"
//             onClick={() => setLightboxIndex(null)}
//             aria-label="Close"
//           >
//             ✕
//           </button>

//           <button
//             className="gal-lightbox-arrow gal-lightbox-arrow--prev"
//             aria-label="Previous photo"
//             onClick={(e) => {
//               e.stopPropagation();
//               setLightboxIndex(
//                 (i) =>
//                   (i - 1 + currentDay?.photos?.length) %
//                   currentDay?.photos?.length,
//               );
//             }}
//           >
//             ‹
//           </button>

//           <figure
//             className="gal-lightbox-figure"
//             onClick={(e) => e.stopPropagation()}
//           >
//             <img
//               src={currentDay?.photos[lightboxIndex]}
//               alt={`${currentDay.title} photo ${lightboxIndex + 1}`}
//             />
//             <figcaption
//               className="gal-lightbox-caption"
//               style={{ "--accent": currentColor.bg }}
//             >
//               {currentDay?.title} — {lightboxIndex + 1} /{" "}
//               {currentDay?.photos?.length}
//             </figcaption>
//           </figure>

//           <button
//             className="gal-lightbox-arrow gal-lightbox-arrow--next"
//             aria-label="Next photo"
//             onClick={(e) => {
//               e.stopPropagation();
//               setLightboxIndex((i) => (i + 1) % currentDay?.photos?.length);
//             }}
//           >
//             ›
//           </button>
//         </div>
//       )}
//     </div>
//   );
// }

// function Breadcrumb({ items }) {
//   return (
//     <nav className="gal-breadcrumb" aria-label="Breadcrumb">
//       <HomeIcon />
//       {items.map((item, i) => (
//         <span className="gal-breadcrumb-seg" key={i}>
//           <span className="gal-breadcrumb-sep">/</span>
//           {item.onClick ? (
//             <button className="gal-breadcrumb-link" onClick={item.onClick}>
//               {item.label}
//             </button>
//           ) : (
//             <span className="gal-breadcrumb-current">{item.label}</span>
//           )}
//         </span>
//       ))}
//     </nav>
//   );
// }

// function ArrowIcon({ className }) {
//   return (
//     <svg
//       className={className}
//       viewBox="0 0 100 60"
//       fill="none"
//       aria-hidden="true"
//     >
//       <path
//         d="M10 22 L55 22 L55 12 L90 30 L55 48 L55 38 L10 38 Z"
//         stroke="currentColor"
//         strokeWidth="4"
//         strokeLinejoin="round"
//         strokeLinecap="round"
//       />
//     </svg>
//   );
// }

// function HomeIcon() {
//   return (
//     <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
//       <path
//         d="M3 9.5L10 3.5l7 6V16a1 1 0 0 1-1 1h-3.5a.5.5 0 0 1-.5-.5V13a2 2 0 0 0-4 0v3.5a.5.5 0 0 1-.5.5H4a1 1 0 0 1-1-1V9.5Z"
//         stroke="currentColor"
//         strokeWidth="1.4"
//         strokeLinejoin="round"
//       />
//     </svg>
//   );
// }
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./Gallery.css";
import { webServiceUrl } from "../../../../components/apiCall/webserviceurl";

/**
 * Gallery
 * ────────────────────────────────────────────────────────────────
 * A day-by-day photo journal.
 *
 * - Top nav shows chips for every day. Clicking a chip FILTERS the
 *   grid down to that single day (no more scroll-to-section — only
 *   the selected day's photos render below).
 * - The photo grid is a masonry / justified layout (CSS columns),
 *   like Google Images or Pinterest: each photo keeps its own
 *   natural aspect ratio instead of being cropped into a fixed box,
 *   so portrait shots stay tall and landscape shots stay wide.
 *
 * Days come from POST /getLessonPlanDays, and each day's photos come
 * from GET /get-gallery-photo?day=<date> (fetched lazily, the first
 * time that day's chip is opened).
 */

// Base host that serves uploaded photo files. photo_path values from
// /get-gallery-photo come back as paths relative to this
// (e.g. "/Screenshot(49)-...png"), not full URLs.
const GALLERY_UPLOADS_BASE_URL =
  "https://engine.ihfc.trainer.mcitiservices.com/uploads";

// Cycles through these for any number of days — accent + text pairs
const DAY_COLORS = [
  { bg: "#16a34a", text: "#fff" }, // dawn amber — Day 1
  { bg: "#16a34a", text: "#fff" }, // meadow teal — Day 2
  { bg: "#16a34a", text: "#fff" }, // dusk indigo — Day 3
  { bg: "#16a34a", text: "#fff" }, // clay rose — Day 4
];

// Fixed identity payload used by /getLessonPlanDays.
const LESSON_PLAN_IDENTITY = {
  planned_on: "2026-03-26",
  school_code: "900",
};

// A photo item from /get-gallery-photo might come back as a plain URL
// string, an object with the URL already under some field, OR (the
// REAL shape, confirmed from the API response) an object carrying a
// relative `photo_path` that still needs the uploads base URL
// prepended. This covers all of those without guessing wrong.
const normalizePhotoUrl = (photo) => {
  if (typeof photo === "string") {
    // Even a bare string might be a relative path rather than a full URL.
    return photo.startsWith("http")
      ? photo
      : `${GALLERY_UPLOADS_BASE_URL}${photo.startsWith("/") ? "" : "/"}${photo}`;
  }

  // Already-absolute URL under one of these common field names — use as-is.
  const directUrl =
    photo?.url || photo?.image || photo?.photo_url || photo?.src || "";
  if (directUrl) return directUrl;

  // Real API shape: { photo_path: "/Screenshot(49)-....png", ... }
  // photo_path is relative to GALLERY_UPLOADS_BASE_URL, so build the
  // full URL here instead of leaving it as a bare, unusable path.
  const relativePath = photo?.photo_path || "";
  if (!relativePath) return "";

  return `${GALLERY_UPLOADS_BASE_URL}${relativePath.startsWith("/") ? "" : "/"}${relativePath}`;
};

export default function Gallery() {
  let token = localStorage.getItem("token");

  const [days, setDays] = useState([]);
  const [daysLoading, setDaysLoading] = useState(true);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState("");
  const [lightboxIndex, setLightboxIndex] = useState(null); // index into the CURRENT day's photos | null
  let getCourse = localStorage.getItem("course_co");
  let course_get = JSON.parse(getCourse || "{}");

  // NULL-SAFE: currentDay can legitimately be null (before days load,
  // or if the API returns zero days) — every consumer below must
  // handle that instead of assuming days[0] always exists.
  const currentDay = useMemo(
    () => days.find((d) => d.id === selectedDay) ?? days[0] ?? null,
    [days, selectedDay],
  );
  const currentDayIndex = currentDay
    ? days.findIndex((d) => d.id === currentDay.id)
    : -1;
  const currentColor =
    DAY_COLORS[currentDayIndex % DAY_COLORS.length] ?? DAY_COLORS[0];

  // ---------------------------------------------------------------------
  // DAYS — POST /getLessonPlanDays
  // Returns the list of dates for the program. Each date becomes a chip
  // ("Day 1", "Day 2", ...); its photos are loaded lazily (see below).
  // ---------------------------------------------------------------------
  const getLessonPlanDays = async () => {
    try {
      setDaysLoading(true);
      const res = await axios.post(
        webServiceUrl.getLessonPlanDays,
        {
          ...LESSON_PLAN_IDENTITY,
          class_name: course_get?.class,
          section: course_get?.section,
          level: course_get?.level,
          combination_code: course_get?.combination_code,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Real shape: { success: true, data: [{ day: "2025-08-28" }, ...] }
      const rawDays = res?.data?.data || [];

      if (Array.isArray(rawDays) && rawDays.length > 0) {
        const mappedDays = rawDays.map((d, idx) => ({
          id: `day-${idx + 1}`,
          title: `Day ${idx + 1}`,
          date: d?.day || "", // "YYYY-MM-DD" — passed straight to /get-gallery-photo
          photos: [], // filled in by loadPhotosForDay() below
        }));
        setDays(mappedDays);
        setSelectedDay(mappedDays[0].id);
        // Load the first day's photos right away.
        loadPhotosForDay(mappedDays[0].id, mappedDays[0].date);
      } else {
        // API responded but with no days — make sure we don't stay
        // stuck thinking we still have data coming.
        setDays([]);
      }
    } catch (e) {
      console.log(e, "ERROR coming from getLessonPlanDays");
      setDays([]);
    } finally {
      setDaysLoading(false);
    }
  };

  // ---------------------------------------------------------------------
  // PHOTOS — GET /get-gallery-photo?day=<date>
  // Fetched lazily: only the first time a given day's chip is opened,
  // then cached on that day's `photos` array.
  //
  // Real response shape (confirmed):
  // {
  //   success: true,
  //   statusCode: 200,
  //   data: {
  //     day: "2026-08-07",
  //     count: 1,
  //     photos: [
  //       { photo_id, day, photo_path, uploaded_at, uploaded_by_name }
  //     ]
  //   }
  // }
  // i.e. the photo array lives at res.data.data.photos, NOT
  // res.data.data directly — data.data is an object, not an array.
  // ---------------------------------------------------------------------
  const getGalleryPhotos = async (dateStr) => {
    if (!dateStr) return [];
    try {
      const res = await axios.get(
        `${webServiceUrl.get_gallery_photo}?day=${dateStr}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      console.log(res, "<<<<<<photos>>>>>>>");

      // Support the real nested shape (data.data.photos) as well as a
      // couple of flatter fallbacks, in case the endpoint's shape
      // varies between environments.
      const rawPhotos =
        res?.data?.data?.photos || res?.data?.data || res?.data?.photos || [];

      return Array.isArray(rawPhotos) ? rawPhotos.map(normalizePhotoUrl) : [];
    } catch (e) {
      console.log(e, "ERROR coming from getGalleryPhotos");
      return [];
    }
  };

  const loadPhotosForDay = async (dayId, dateStr) => {
    if (!dateStr) return;
    setPhotosLoading(true);
    const photos = await getGalleryPhotos(dateStr);
    if (photos.length > 0) {
      setDays((prev) =>
        prev.map((d) => (d.id === dayId ? { ...d, photos } : d)),
      );
    }
    setPhotosLoading(false);
  };

  const handleSelectDay = (day) => {
    setSelectedDay(day.id);
    if (day?.photos?.length === 0) {
      loadPhotosForDay(day.id, day.date);
    }
  };

  useEffect(() => {
    if(!!course_get)
    getLessonPlanDays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset the lightbox whenever the selected day changes.
  useEffect(() => {
    setLightboxIndex(null);
  }, [selectedDay]);

  // Lightbox keyboard controls: Escape closes, arrows step through
  // photos within the currently selected day.
  useEffect(() => {
    if (lightboxIndex === null || !currentDay) return;
    const total = currentDay?.photos?.length;
    if (!total) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowRight") setLightboxIndex((i) => (i + 1) % total);
      if (e.key === "ArrowLeft")
        setLightboxIndex((i) => (i - 1 + total) % total);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [lightboxIndex, currentDay]);

  console.log(currentDay);

  return (
    <div className="gal-page">
      {/* ---- LARGER PHOTO TILES ----
          Gallery.css isn't available to edit directly here, so this
          forces bigger tiles/columns with !important so it applies
          regardless of what's already defined there. Assumes the grid
          is CSS-columns based (masonry-style) per the component's own
          comments above — if Gallery.css actually uses CSS Grid with
          grid-template-columns instead, share that file so this can
          be edited at the source instead of layered on top. */}
      <style>{`
        .gal-day-grid {
          column-width: 340px !important;
          column-gap: 16px !important;
        }
        .gal-day-item {
          width: 100% !important;
          margin-bottom: 16px !important;
          break-inside: avoid;
        }
        .gal-day-item img {
          width: 100% !important;
          height: auto !important;
          min-height: 240px !important;
          object-fit: cover !important;
          display: block;
        }
        @media (max-width: 767.98px) {
          .gal-day-grid {
            column-width: 260px !important;
          }
          .gal-day-item img {
            min-height: 180px !important;
          }
        }
        @media (max-width: 480px) {
          .gal-day-grid {
            column-width: 100% !important;
          }
        }
      `}</style>

      <div className="gal-panel">
        {/* <h2 className="gal-panel-title">Gallery</h2> */}

        <div className="gal-nav" role="tablist" aria-label="Choose a day">
          {days.map((day, i) => {
            const color = DAY_COLORS[i % DAY_COLORS.length];
            const isActive = selectedDay === day.id;
            return (
              <div className="gal-nav-item" key={day.id}>
                <button
                  role="tab"
                  aria-selected={isActive}
                  className={`gal-nav-chip${isActive ? " gal-nav-chip--active" : ""}`}
                  style={{ "--accent": color.bg }}
                  onClick={() => handleSelectDay(day)}
                >
                  {day?.title}
                  <span className="gal-nav-chip-count">
                    {day?.photos?.length}
                  </span>
                </button>
                {i < days?.length - 1 && (
                  <ArrowIcon className="gal-nav-arrow" />
                )}
              </div>
            );
          })}
        </div>

        {/* NULL-SAFE: bail out here instead of ever touching
            currentDay.id when currentDay is null (still loading, or
            the API returned zero days). This is what was crashing. */}
        {!currentDay ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px 0",
              color: "#6b7280",
            }}
          >
            {daysLoading ? "Loading days…" : "No gallery days found."}
          </div>
        ) : (
          <section className="gal-day" key={currentDay.id}>
            {/* <header className="gal-day-header">
              <span
                className="gal-day-tab"
                style={{ background: currentColor.bg, color: currentColor.text }}
              >
                {String(currentDayIndex + 1).padStart(2, "0")}
              </span>
              <div className="gal-day-heading">
                <h3 className="gal-day-title">{currentDay.title}</h3>
                <span className="gal-day-meta">
                  {currentDay.photos.length} photo
                  {currentDay.photos.length === 1 ? "" : "s"}
                </span>
              </div>
            </header> */}

            {photosLoading && currentDay?.photos?.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 0",
                  color: "#6b7280",
                }}
              >
                Loading photos…
              </div>
            ) : (
              <div className="gal-day-grid">
                {currentDay?.photos?.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 0",
                      color: "#9ca3af",
                      fontSize: "16px",
                      fontWeight: "500",
                    }}
                  >
                    No images found for {currentDay.title}
                  </div>
                ) : (
                  <div className="gal-day-grid">
                    {currentDay.photos.map((src, j) => (
                      <button
                        type="button"
                        className="gal-day-item"
                        key={j}
                        style={{ "--accent": currentColor.bg }}
                        onClick={() => setLightboxIndex(j)}
                        aria-label={`Open photo ${j + 1} from ${currentDay.title}`}
                      >
                        <img
                          src={src}
                          alt={`${currentDay.title} photo ${j + 1}`}
                          loading="lazy"
                        />
                        <span className="gal-day-item-caption">
                          {currentDay.title} · {j + 1}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
        )}
      </div>

      {/* NULL-SAFE: only render the lightbox if we actually have a
          currentDay to reference. */}
      {lightboxIndex !== null && currentDay && (
        <div
          className="gal-lightbox"
          onClick={() => setLightboxIndex(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`${currentDay.title} photo ${lightboxIndex + 1}`}
        >
          <button
            className="gal-lightbox-close"
            onClick={() => setLightboxIndex(null)}
            aria-label="Close"
          >
            ✕
          </button>

          <button
            className="gal-lightbox-arrow gal-lightbox-arrow--prev"
            aria-label="Previous photo"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIndex(
                (i) =>
                  (i - 1 + currentDay?.photos?.length) %
                  currentDay?.photos?.length,
              );
            }}
          >
            ‹
          </button>

          <figure
            className="gal-lightbox-figure"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={currentDay?.photos[lightboxIndex]}
              alt={`${currentDay.title} photo ${lightboxIndex + 1}`}
            />
            <figcaption
              className="gal-lightbox-caption"
              style={{ "--accent": currentColor.bg }}
            >
              {currentDay?.title} — {lightboxIndex + 1} /{" "}
              {currentDay?.photos?.length}
            </figcaption>
          </figure>

          <button
            className="gal-lightbox-arrow gal-lightbox-arrow--next"
            aria-label="Next photo"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIndex((i) => (i + 1) % currentDay?.photos?.length);
            }}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}

function Breadcrumb({ items }) {
  return (
    <nav className="gal-breadcrumb" aria-label="Breadcrumb">
      <HomeIcon />
      {items.map((item, i) => (
        <span className="gal-breadcrumb-seg" key={i}>
          <span className="gal-breadcrumb-sep">/</span>
          {item.onClick ? (
            <button className="gal-breadcrumb-link" onClick={item.onClick}>
              {item.label}
            </button>
          ) : (
            <span className="gal-breadcrumb-current">{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

function ArrowIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 100 60"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M10 22 L55 22 L55 12 L90 30 L55 48 L55 38 L10 38 Z"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function HomeIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
      <path
        d="M3 9.5L10 3.5l7 6V16a1 1 0 0 1-1 1h-3.5a.5.5 0 0 1-.5-.5V13a2 2 0 0 0-4 0v3.5a.5.5 0 0 1-.5.5H4a1 1 0 0 1-1-1V9.5Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}
